import api, { ApiError } from '@anm/api';
import { Webinar } from '@anm/api/modules/hosting';
import { parseManifest } from '@anm/hosting-player';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LiveStream } from 'types/stream';

import useFetchUserPrefs from '../../../../hooks/useFetchUserPrefs';
import { hostedVideoActions, hostedVideoSelectors } from '../../../../store/modules/hostedVideo';
import { setVideoData } from '../../../../store/modules/hostedVideo/actions';
import { landingsActions, landingsSelectors } from '../../../../store/modules/landings';
import { userActions, userSelectors } from '../../../../store/modules/user';
import { useFetchBranding, usePrefferedBrandId } from '../../../hosting/CustomizationSection/tabs/LandingTab/hooks';

const useFetchWebinarData = (webinarId: string) => {
  const dispatch = useDispatch();

  const [stream, setStream] = useState<LiveStream>();
  const [isStreamError, setStreamError] = useState<ApiError>();
  const [isStreamPending, setStreamPending] = useState(true);

  const webinar = useSelector(landingsSelectors.selectLanding) as Webinar;
  const manifest = useSelector(hostedVideoSelectors.selectManifest);

  const webinarError = useSelector(landingsSelectors.selectGetLandingError);
  const manifestError = useSelector(hostedVideoSelectors.selectGetManifestError);

  const isWebinarPending = useSelector(landingsSelectors.selectGetLandingPendingStatus);
  const isManifestPending = useSelector(hostedVideoSelectors.selectGetManifestPending);

  const userEmbeds = useSelector(userSelectors.selectUserEmbeds);

  useFetchBranding();
  useFetchUserPrefs();
  usePrefferedBrandId();

  useEffect(() => {
    if (manifest?.id === webinarId) return;

    dispatch(hostedVideoActions.getManifest(webinarId));
  }, [webinarId, manifest?.id]);

  useEffect(() => {
    if (!manifest) return;

    dispatch(
      setVideoData({
        hostId: webinarId,
        videoData: parseManifest(manifest),
        embedStatus: !!manifest.isEmbedded
      })
    );
  }, [manifest]);

  useEffect(() => {
    if (webinar?.hostId === webinarId) return;

    dispatch(
      landingsActions.getLanding({
        hostId: webinarId
      })
    );
  }, [webinarId, webinar?.hostId]);

  useEffect(() => {
    !userEmbeds && dispatch(userActions.fetchEmbedStats());
  }, [userEmbeds]);

  useEffect(() => {
    if (!webinar?.parameters.streamId) return;

    api()
      .stream.getPublicStream(webinar?.parameters.streamId)
      .then(stream => setStream(stream))
      .catch(e => setStreamError(e))
      .finally(() => setStreamPending(false));
  }, [webinar?.parameters.streamId]);

  const error = webinarError || manifestError || isStreamError;
  const isPending = isWebinarPending || isManifestPending || isStreamPending;
  const canRender = !!(webinar && manifest && stream && !error);

  return [stream, error, isPending, canRender] as const;
};

export default useFetchWebinarData;
